import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { Menu, X, Home, ChevronDown, Shield } from "lucide-react";
import { Button } from "./ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";

const LANG_LABEL = { fr: "FR", ar: "العربية", en: "EN" };

export default function Header() {
  const { lang, setLang, t } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed top-0 w-full z-50 backdrop-blur-xl bg-white/90 border-b border-slate-200/70">
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="header-logo">
          <div className="w-9 h-9 rounded-xl bg-[#1B8F5A] flex items-center justify-center">
            <Home size={17} className="text-white" strokeWidth={2.5} />
          </div>
          <span className="text-xl font-bold tracking-tight text-[#111827]">Naydars</span>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          <Link to="/search?transaction=sale" className="text-sm font-medium text-slate-700 hover:text-[#1B8F5A] transition" data-testid="nav-buy">{t("buy")}</Link>
          <Link to="/search?transaction=rent" className="text-sm font-medium text-slate-700 hover:text-[#1B8F5A] transition" data-testid="nav-rent">{t("rent")}</Link>
          <Link to="/for-agencies" className="text-sm font-medium text-slate-700 hover:text-[#1B8F5A] transition" data-testid="nav-for-agencies">{t("forAgencies")}</Link>
          {user && user.role === "admin" && (
            <Link to="/admin" className="text-sm font-medium text-[#1B8F5A] hover:text-[#157047] transition flex items-center gap-1" data-testid="nav-admin">
              <Shield size={14} />Admin
            </Link>
          )}
          {user && (
            <Link to="/dashboard" className="text-sm font-medium text-slate-700 hover:text-[#1B8F5A] transition" data-testid="nav-dashboard">{t("dashboard")}</Link>
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="text-xs font-semibold px-3 py-1.5 rounded-full border border-slate-200 hover:border-[#1B8F5A] hover:text-[#1B8F5A] transition flex items-center gap-1" data-testid="lang-toggle">
                {LANG_LABEL[lang]} <ChevronDown size={12} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {["fr", "ar", "en"].map((l) => (
                <DropdownMenuItem key={l} onClick={() => setLang(l)} data-testid={`lang-${l}`}>
                  {LANG_LABEL[l]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <>
              <Link to="/dashboard" className="text-sm text-slate-600 hover:text-[#1B8F5A]" data-testid="user-name">{user.name}</Link>
              <Button onClick={() => { logout(); navigate("/"); }} variant="outline" size="sm" data-testid="logout-btn">{t("logout")}</Button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm font-medium text-slate-700 hover:text-[#1B8F5A]" data-testid="login-link">{t("login")}</Link>
              <Link to="/post" data-testid="register-link">
                <Button size="sm" className="bg-[#1B8F5A] hover:bg-[#157047] rounded-full px-5">{t("postAd")}</Button>
              </Link>
            </>
          )}
        </div>

        <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-100 bg-white px-6 py-4 space-y-3">
          <Link to="/search?transaction=sale" className="block text-sm" onClick={() => setOpen(false)}>{t("buy")}</Link>
          <Link to="/search?transaction=rent" className="block text-sm" onClick={() => setOpen(false)}>{t("rent")}</Link>
          <Link to="/for-agencies" className="block text-sm font-semibold text-[#1B8F5A]" onClick={() => setOpen(false)}>{t("forAgencies")}</Link>
          {user ? (
            <>
              <Link to="/dashboard" className="block text-sm" onClick={() => setOpen(false)}>{t("dashboard")}</Link>
              <Link to="/post" className="block text-sm" onClick={() => setOpen(false)}>{t("postAd")}</Link>
              <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="block text-sm">{t("logout")}</button>
            </>
          ) : (
            <>
              <Link to="/login" className="block text-sm" onClick={() => setOpen(false)}>{t("login")}</Link>
              <Link to="/register" className="block text-sm font-semibold text-[#1B8F5A]" onClick={() => setOpen(false)}>{t("register")}</Link>
            </>
          )}
          <div className="flex gap-2 pt-2">
            {["fr", "ar", "en"].map((l) => (
              <button key={l} onClick={() => setLang(l)} className={`text-xs px-3 py-1.5 rounded-full border ${lang === l ? "border-[#1B8F5A] text-[#1B8F5A]" : "border-slate-200"}`}>{LANG_LABEL[l]}</button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
